import { RefObject, useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { useDrawerGuard } from '../../../cdl/Drawer/useDrawerGuard';
import { Label } from '../../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { Text } from '../../../cdl/Text/Text';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { startOfTimeZoneDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useSchedules } from '../../../common/hooks/useSchedules';
import { Box } from '../../../common/ui/Box';
import { SuggestionDrawerContent } from '../../create-enquiry/components/SuggestionDrawerContent';
import { usePortSuggestionsStream } from '../../create-enquiry/hooks/usePortSuggestionsStream';
import { useOrder } from '../../hooks/useOrder';

enum SupplierSelection {
    DEFAULT = 'DEFAULT',
    CONTRACTED = 'CONTRACTED',
    ALL = 'ALL',
}

export interface SuggestionData {
    portId: string;
    dateDelivery: string;
    receiverSupplierIds: string[];
    spot: boolean;
}

interface SuggestionDrawerProps {
    orderId: string;
    onSaveDraft: (data: SuggestionData) => void;
    onSendEnquiry: (data: SuggestionData) => void;
    onSuggestionFinished: () => void;
    isDraftUpdatePending: RefObject<boolean>;
}

export const SuggestionDrawer = ({
    orderId,
    onSaveDraft,
    onSendEnquiry,
    onSuggestionFinished,
    isDraftUpdatePending,
}: SuggestionDrawerProps) => {
    const streamQuery = usePortSuggestionsStream(orderId);
    const { data: order } = useOrder(orderId);
    const { schedules } = useSchedules({ imo: order?.vessel?.imo });
    const { pop, layerId } = useDrawer();
    const [selectedSupplierOption, setSelectedSupplierOption] = useState<SupplierSelection>(SupplierSelection.DEFAULT);

    useDrawerGuard({ hasUnsavedChanges: selectedSupplierOption !== SupplierSelection.DEFAULT });

    streamQuery.setCompletedCallback(onSuggestionFinished);

    const radioGroupOptions = [
        {
            value: SupplierSelection.DEFAULT,
            label: translate('closelinkInsights.supplierSelection.defaultSupplierOnly'),
        },
        {
            value: SupplierSelection.CONTRACTED,
            label: translate('closelinkInsights.supplierSelection.allContractedSuppliers'),
        },
        { value: SupplierSelection.ALL, label: translate('closelinkInsights.supplierSelection.allSuppliersInclSpot') },
    ];

    const findDateDeliveryFromSchedule = (stopId: string): string | null => {
        if (!schedules || !schedules.length) {
            return null;
        }

        const matchingSchedule = schedules.find((schedule) => schedule.id === stopId);

        if (!matchingSchedule) {
            return null;
        }

        return startOfTimeZoneDayAsUtcIso(matchingSchedule.eta, matchingSchedule.timeZoneId);
    };

    const prepareSuggestionData = (): SuggestionData | null => {
        if (!streamQuery.finalResult) {
            return null;
        }

        const { portId, stopId } = streamQuery.finalResult;
        const dateDelivery = findDateDeliveryFromSchedule(stopId);

        if (!dateDelivery) {
            return null;
        }

        // Prepare supplier selection based on radio choice
        let receiverSupplierIds: string[] = [];
        let spot = false;

        const defaultSupplierId = order?.vessel?.defaultSupplierId;
        const assignedSupplierIds = order?.assignedSupplierIds || [];

        switch (selectedSupplierOption) {
            case SupplierSelection.DEFAULT:
                receiverSupplierIds = defaultSupplierId ? [defaultSupplierId] : [];
                spot = false;
                break;
            case SupplierSelection.CONTRACTED:
                receiverSupplierIds = assignedSupplierIds;
                spot = false;
                break;
            case SupplierSelection.ALL:
                receiverSupplierIds = assignedSupplierIds;
                spot = true;
                break;
        }

        return {
            portId,
            dateDelivery,
            receiverSupplierIds,
            spot,
        };
    };

    const handleSaveDraft = () => {
        const suggestionData = prepareSuggestionData();
        if (suggestionData && onSaveDraft) {
            onSaveDraft(suggestionData);
            pop(layerId);
        }
    };

    const handleSendEnquiry = () => {
        const suggestionData = prepareSuggestionData();
        if (suggestionData && onSendEnquiry) {
            onSendEnquiry(suggestionData);
            pop(layerId);
        }
    };

    return (
        <Box
            display="grid"
            gridTemplateRows="minmax(0, 1fr) auto"
            height="100%"
            minHeight={0}
            gridTemplateColumns="100%"
        >
            <SuggestionDrawerContent
                query={streamQuery}
                isDraftUpdatePending={isDraftUpdatePending?.current || false}
            />

            {streamQuery.finalResult?.success ? (
                <Box padding={24} display="grid" gap={16} boxShadow="large">
                    <Box display="flex" alignItems="flex-start" flexDirection="column" gap={4}>
                        <Text variant="fieldLabel" color="foreground.muted">
                            {translate('closelinkInsights.supplierSelection')}
                        </Text>

                        <RadioGroup
                            value={selectedSupplierOption}
                            onValueChange={(value) => setSelectedSupplierOption(value as SupplierSelection)}
                        >
                            {radioGroupOptions.map((option) => (
                                <RadioGroupItem key={option.value}>
                                    <Radio value={option.value} />
                                    <Label>{option.label}</Label>
                                </RadioGroupItem>
                            ))}
                        </RadioGroup>
                    </Box>

                    <ButtonGroup justifyContent="flex-end">
                        <Button onClick={handleSaveDraft}>{translate('closelinkInsights.saveDraft')}</Button>
                        <Button emphasis="high" onClick={handleSendEnquiry}>
                            {translate('closelinkInsights.sendEnquiry')}
                        </Button>
                    </ButtonGroup>
                </Box>
            ) : null}
        </Box>
    );
};
